import {
	Component,
	ElementRef,
	EventEmitter,
	Input,
	OnInit,
	OnChanges,
	OnDestroy,
	Output,
	SimpleChanges,
	ViewChild,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { IonContent } from '@ionic/angular';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { ProfileRelatedInfoBean } from 'src/app/beans/account/account-module.bean';

@Component({
	selector: 'app-ehe-layout',
	templateUrl: './layout.component.html',
	styleUrls: ['./layout.component.scss'],
})
export class LayoutComponent implements OnInit, OnChanges, OnDestroy {
	// IonContent reference used by child-components (like app-question) to control scrolling
	@ViewChild('contentRef', { static: true }) contentRef!: IonContent;
	@ViewChild('tabsBar') tabsBar!: ElementRef<HTMLElement>;

	/** denote whether to show spinner or not */
	@Input() spinner: boolean = true;
	/** denote whether to show footer or not */
	@Input() footer: boolean = true;
	/** denote whether to show header or not */
	@Input() header: boolean = true;
	@Input() hideLinks: boolean = false;
	/** whether to show the top bar (set false on pages with their own header) */
	@Input() showTopbar: boolean = true;

	// Footer related inputs

	/** denote whether to show social network links or not */
	@Input() socialNetwork: boolean = false;

	// Header related inputs
	/** isSimplified */
	@Input() isSimplified: boolean = false;
	/** To show or hide phone number on header */
	@Input() showPhone: boolean = false;
	/** denote auth flag, helps to show different headers */
	@Input() auth: boolean = false;
	/** To show or hide phone number on header */
	@Input() wellness: boolean = false;
	/** denote auth flag, helps to show different headers */
	@Input() healthHistory: boolean = false;
	/** To show or hide mobile back button */
	@Input() showMobileBackButton: boolean = false;

	sidebarOpen: boolean = false;
	sidebarCollapsed: boolean = true;

	readonly wellnessTabs = [
		{ label: 'Health Records', value: '/results' },
		{ label: 'Vaccine Records', value: '/vaxstatus' },
		{ label: 'Referrals', value: '/wellness/referral' },
		{ label: 'Health Assessment', value: '/assessment' },
	];

	private readonly _wellnessRoutes = this.wellnessTabs.map(t => t.value);

	get wellnessTabsVisible(): boolean {
		const path = this.router.url.split('?')[0];
		return this._wellnessRoutes.some(r => path === r || path.startsWith(r + '/'));
	}

	get activeWellnessTab(): string {
		const path = this.router.url.split('?')[0];
		return this._wellnessRoutes.find(r => path === r || path.startsWith(r + '/')) ?? '';
	}

	navigateWellnessTab(route: string): void {
		this.router.navigate([route]);
	}

	private scrollActiveTabIntoView(): void {
		setTimeout(() => {
			const activeTab = this.tabsBar?.nativeElement?.querySelector<HTMLElement>('.wellness-tab-item.active');
			activeTab?.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
		});
	}

	private _destroyed$ = new Subject<boolean>();

	/** sharing profile related info to other components */
	@Output() profileRelatedInfo: EventEmitter<ProfileRelatedInfoBean> = new EventEmitter<ProfileRelatedInfoBean>();

	constructor(private router: Router) {}

	ngOnInit(): void {
		// Sidebar is always in collapsed (icon-only) mode
		this.sidebarCollapsed = true;

		// Handle sidebar open state based on screen size
		// Desktop: sidebar is always open
		// Mobile: sidebar starts closed (user can toggle it)
		this.sidebarOpen = window.innerWidth >= 992;

		this.updateBodySidebarWidth();

		// On mobile/tablet, close sidebar after every navigation (handles Ionic page caching).
		// Also triggers change detection so wellnessTabsVisible / activeWellnessTab getters
		// re-evaluate correctly on every navigation including Ionic cached page restores.
		this.router.events
			.pipe(
				filter(e => e instanceof NavigationEnd),
				takeUntil(this._destroyed$)
			)
			.subscribe(() => {
				this.contentRef.scrollToTop(0);
				if (window.innerWidth < 992) {
					this.sidebarOpen = false;
				}
				this.scrollActiveTabIntoView();
			});
	}

	ngOnChanges(_changes: SimpleChanges): void {
		this.updateBodySidebarWidth();
	}

	private updateBodySidebarWidth(): void {
		const isDesktop = typeof window !== 'undefined' && window.innerWidth >= 992;
		if (this.auth && isDesktop) {
			document.body.style.setProperty('--sidebar-width', '120px');
		} else {
			document.body.style.setProperty('--sidebar-width', '0px');
		}
	}

	toggleSidebar(): void {
		this.sidebarOpen = !this.sidebarOpen;
		// On mobile/tablet, always show fully expanded sidebar (never icon-only)
		if (this.sidebarOpen && window.innerWidth < 992) {
			this.sidebarCollapsed = false;
		}
	}

	ngOnDestroy(): void {
		this._destroyed$.next(true);
		this._destroyed$.unsubscribe();
	}

	collapseSidebar(action: 'toggle' | 'close'): void {
		// On mobile/tablet, collapse toggles the sidebar open/closed
		if (window.innerWidth < 992) {
			if (action === 'toggle') {
				this.sidebarOpen = !this.sidebarOpen;
			} else {
				this.sidebarOpen = false;
			}
			return;
		}
		// Desktop sidebar stays always collapsed (icon-only)
	}
}
